import { formatDisplayHeaderLines } from './http-pseudo-header.utils';
import {
  normalizeHarHttpVersion,
  normalizeHttpVersionNumber,
  resolveEffectiveHttpVersion,
} from './http-version.utils';

export type HttpHeaderRecord = Record<string, string | string[]>;

export type ParsedHttpRequest = {
  method: string;
  url: string;
  path: string;
  httpVersion: string;
  headers: HttpHeaderRecord;
  rawHeaders?: string[];
  body: string;
};

export type ParsedHttpResponse = {
  httpVersion: string;
  statusCode: number;
  statusMessage: string;
  headers: HttpHeaderRecord;
  rawHeaders?: string[];
  body: string;
};

export type ParsedHttpResponsePacket = ParsedHttpResponse & {
  bodyBuffer?: Uint8Array;
  rawPacket?: Uint8Array;
};

export type ParseHttpRequestOptions = {
  originalUrl?: string;
  captureRawHeaders?: boolean;
};

export type ParseHttpResponseOptions = {
  captureRawHeaders?: boolean;
};

export type FormatHttpMessageOptions = {
  lineEnding?: '\n' | '\r\n';
};

const HTTP_STATUS_TEXT_BY_CODE: Record<number, string> = {
  200: 'OK',
  201: 'Created',
  202: 'Accepted',
  204: 'No Content',
  301: 'Moved Permanently',
  302: 'Found',
  304: 'Not Modified',
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  405: 'Method Not Allowed',
  409: 'Conflict',
  422: 'Unprocessable Entity',
  429: 'Too Many Requests',
  500: 'Internal Server Error',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
};

export function getHttpStatusText(statusCode: number): string {
  return HTTP_STATUS_TEXT_BY_CODE[statusCode] ?? 'Unknown';
}

export type HttpRequestModel = {
  method: string;
  url: string;
  headers: HttpHeaderRecord;
  rawHeaders?: string[];
  body?: string;
  httpVersion: string;
};

export type HttpResponseModel = {
  statusCode: number;
  statusMessage?: string;
  headers: HttpHeaderRecord;
  rawHeaders?: string[];
  body?: string;
  httpVersion: string;
};

export type ParseHttpRequestResult =
  | { success: true; data: ParsedHttpRequest }
  | { success: false; error: string; isResponse: boolean };

const HTTP_PSEUDO_HEADER_NAME_REGEX = /^:[a-z0-9-]+$/;
const HTTP_TOKEN_REGEX = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;
const HTTP_VERSION_NUMBER_REGEX = /^[0-9]+\.[0-9]+$/;
const HTTP_RESPONSE_LINE_REGEX = /^HTTP\/[0-9]+(?:\.[0-9]+)?\s+\d{3}(?:\s|$)/i;
const HTTP_REQUEST_LINE_REGEX =
  /^([!#$%&'*+\-.^_`|~0-9A-Za-z]+)\s+(\S+)\s+HTTP\/([0-9]+(?:\.[0-9]+)?)$/;
const CONNECT_AUTHORITY_TARGET_REGEX = /^[^\s/]+:\d+$/;
const STATUS_LINE_REGEX = /^HTTP\/([0-9]+(?:\.[0-9]+)?)\s+(\d{3})(?:\s+(.*))?$/;
const HEADER_NAME_COLON_SEPARATOR_REGEX = /^[^\s:]+[\t ]+:/;

function normalizeParsedHttpVersion(rawVersion: string): string {
  if (HTTP_VERSION_NUMBER_REGEX.test(rawVersion)) {
    return rawVersion;
  }

  return `${rawVersion}.0`;
}

function isHttp2ParsedVersion(httpVersion: string): boolean {
  return httpVersion.startsWith('2.');
}

function findFirstNonEmptyLineIndex(lines: string[]): number {
  for (let index = 0; index < lines.length; index++) {
    if (lines[index].trim() !== '') {
      return index;
    }
  }

  return -1;
}

function parseStrictFormatHttpVersion(httpVersion: string): string {
  const trimmed = httpVersion.trim();
  const versionNumber = trimmed.startsWith('HTTP/')
    ? trimmed.slice(5)
    : trimmed;
  if (!HTTP_VERSION_NUMBER_REGEX.test(versionNumber)) {
    throw new Error('HTTP version must match RFC 7230 HTTP-version format');
  }

  return versionNumber;
}

function normalizeLines(text: string): string[] {
  return text
    .split('\n')
    .map((line) => (line.endsWith('\r') ? line.slice(0, -1) : line));
}

function stripOptionalWhitespace(value: string): string {
  return value.replace(/^[\t ]+/, '').replace(/[\t ]+$/, '');
}

function parseHeaderLine(line: string): { key: string; value: string } | null {
  if (HEADER_NAME_COLON_SEPARATOR_REGEX.test(line)) {
    return null;
  }

  if (line.startsWith(':')) {
    const separatorIndex = line.indexOf(':', 1);
    if (separatorIndex <= 1) {
      return null;
    }
    const key = line.slice(0, separatorIndex);
    if (!HTTP_PSEUDO_HEADER_NAME_REGEX.test(key)) {
      return null;
    }
    return {
      key,
      value: stripOptionalWhitespace(line.slice(separatorIndex + 1)),
    };
  }

  const colonIndex = line.indexOf(':');
  if (colonIndex <= 0) {
    return null;
  }

  const key = line.slice(0, colonIndex);
  if (!HTTP_TOKEN_REGEX.test(key)) {
    return null;
  }

  return {
    key,
    value: stripOptionalWhitespace(line.slice(colonIndex + 1)),
  };
}

function appendHeaderValue(
  headers: HttpHeaderRecord,
  key: string,
  value: string,
): void {
  const existing = headers[key];
  if (existing) {
    headers[key] = Array.isArray(existing)
      ? [...existing, value]
      : [existing, value];
    return;
  }
  headers[key] = value;
}

function appendRawHeader(
  rawHeaders: string[],
  key: string,
  value: string,
): void {
  rawHeaders.push(key, value);
}

function foldHeaderContinuation({
  headers,
  rawHeaders,
  key,
  line,
  captureRawHeaders,
}: {
  headers: HttpHeaderRecord;
  rawHeaders: string[];
  key: string;
  line: string;
  captureRawHeaders: boolean;
}): void {
  const existing = headers[key];
  const foldedSegment = stripOptionalWhitespace(line);
  if (Array.isArray(existing)) {
    const lastIndex = existing.length - 1;
    existing[lastIndex] = `${existing[lastIndex]} ${foldedSegment}`;
  } else {
    headers[key] = `${existing} ${foldedSegment}`;
  }

  if (captureRawHeaders && rawHeaders.length >= 2) {
    const lastValueIndex = rawHeaders.length - 1;
    rawHeaders[lastValueIndex] =
      `${rawHeaders[lastValueIndex]} ${foldedSegment}`;
  }
}

function isHeaderContinuationLine(line: string): boolean {
  return line.startsWith(' ') || line.startsWith('\t');
}

function getOriginalLineLength({
  text,
  line,
  lineStartIndex,
}: {
  text: string;
  line: string;
  lineStartIndex: number;
}): number {
  return text.charAt(lineStartIndex + line.length) === '\r'
    ? line.length + 2
    : line.length + 1;
}

function getFirstHeaderValue(value: string | string[]): string {
  return Array.isArray(value) ? value[0] : value;
}

function extractRequestHostAuthorityScheme(headers: HttpHeaderRecord): {
  hostHeader: string;
  pseudoAuthority: string;
  pseudoScheme: string;
} {
  let hostHeader = '';
  let pseudoAuthority = '';
  let pseudoScheme = '';

  for (const [key, value] of Object.entries(headers)) {
    if (key.toLowerCase() === 'host') {
      hostHeader = getFirstHeaderValue(value);
    } else if (key === ':authority') {
      pseudoAuthority = getFirstHeaderValue(value);
    } else if (key === ':scheme') {
      pseudoScheme = getFirstHeaderValue(value);
    }
  }

  return { hostHeader, pseudoAuthority, pseudoScheme };
}

function resolveProtocolFromExplicitSources({
  pseudoScheme,
  originalUrl,
}: {
  pseudoScheme: string;
  originalUrl?: string;
}): string | null {
  if (pseudoScheme) {
    return pseudoScheme.replace(/:$/, '');
  }

  if (!originalUrl) {
    return null;
  }

  try {
    return new URL(originalUrl).protocol.replace(':', '');
  } catch {
    return null;
  }
}

function isValidRequestTarget(method: string, target: string): boolean {
  if (
    target.startsWith('/') ||
    target.startsWith('http://') ||
    target.startsWith('https://')
  ) {
    return true;
  }

  if (target === '*' && method.toUpperCase() === 'OPTIONS') {
    return true;
  }

  if (
    method.toUpperCase() === 'CONNECT' &&
    CONNECT_AUTHORITY_TARGET_REGEX.test(target)
  ) {
    return true;
  }

  return false;
}

function buildRequestUrl({
  path,
  hostHeader,
  pseudoAuthority,
  pseudoScheme,
  originalUrl,
}: {
  path: string;
  hostHeader: string;
  pseudoAuthority: string;
  pseudoScheme: string;
  originalUrl?: string;
}): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  const selectedHost = hostHeader || pseudoAuthority;
  if (!selectedHost) {
    return path;
  }

  const protocol = resolveProtocolFromExplicitSources({
    pseudoScheme,
    originalUrl,
  });
  if (!protocol) {
    return path;
  }

  return `${protocol}://${selectedHost}${path}`;
}

function extractRequestPath(url: string): string {
  if (url.startsWith('/')) {
    return url;
  }

  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    throw new Error('Request URL must be an absolute URL or origin-form path');
  }
}

function isHttpResponseLine(firstLine: string): boolean {
  return HTTP_RESPONSE_LINE_REGEX.test(firstLine.trim());
}

function parseHeaderBlock({
  lines,
  startIndex,
  text,
  captureRawHeaders,
  enforceHttp2PseudoHeaderOrdering,
}: {
  lines: string[];
  startIndex: number;
  text: string;
  captureRawHeaders: boolean;
  enforceHttp2PseudoHeaderOrdering: boolean;
}): {
  headers: HttpHeaderRecord;
  rawHeaders: string[];
  bodyStartCharIndex: number;
  bodyStartLineIndex: number;
} {
  const headers: HttpHeaderRecord = {};
  const rawHeaders: string[] = [];
  let bodyStartCharIndex = -1;
  let bodyStartLineIndex = -1;
  let currentCharIndex = 0;
  let lastHeaderKey: string | null = null;
  let seenRegularHeader = false;

  for (let index = 0; index < startIndex; index++) {
    currentCharIndex += getOriginalLineLength({
      text,
      line: lines[index],
      lineStartIndex: currentCharIndex,
    });
  }

  for (let index = startIndex; index < lines.length; index++) {
    const line = lines[index];
    const originalLineLength = getOriginalLineLength({
      text,
      line,
      lineStartIndex: currentCharIndex,
    });

    if (line.trim() === '') {
      currentCharIndex += originalLineLength;
      bodyStartCharIndex = currentCharIndex;
      bodyStartLineIndex = index + 1;
      break;
    }

    if (isHeaderContinuationLine(line)) {
      if (lastHeaderKey) {
        foldHeaderContinuation({
          headers,
          rawHeaders,
          key: lastHeaderKey,
          line,
          captureRawHeaders,
        });
        currentCharIndex += originalLineLength;
        continue;
      }

      bodyStartCharIndex = currentCharIndex;
      bodyStartLineIndex = index;
      break;
    }

    if (
      enforceHttp2PseudoHeaderOrdering &&
      seenRegularHeader &&
      line.startsWith(':')
    ) {
      bodyStartCharIndex = currentCharIndex;
      bodyStartLineIndex = index;
      break;
    }

    const parsedHeader = parseHeaderLine(line);
    if (parsedHeader) {
      appendHeaderValue(headers, parsedHeader.key, parsedHeader.value);
      if (captureRawHeaders) {
        appendRawHeader(rawHeaders, parsedHeader.key, parsedHeader.value);
      }
      lastHeaderKey = parsedHeader.key;
      if (!parsedHeader.key.startsWith(':')) {
        seenRegularHeader = true;
      }
    } else {
      bodyStartCharIndex = currentCharIndex;
      bodyStartLineIndex = index;
      break;
    }

    currentCharIndex += originalLineLength;
  }

  return {
    headers,
    rawHeaders,
    bodyStartCharIndex,
    bodyStartLineIndex,
  };
}

function extractMessageBody({
  raw,
  bodyStartCharIndex,
}: {
  raw: string;
  bodyStartCharIndex: number;
}): string {
  if (bodyStartCharIndex >= 0 && bodyStartCharIndex < raw.length) {
    return raw.slice(bodyStartCharIndex);
  }

  return '';
}

export function parseHttpRequest(
  raw: string,
  options: ParseHttpRequestOptions = {},
): ParsedHttpRequest | null {
  const result = parseHttpRequestWithResult(raw, options);
  return result.success ? result.data : null;
}

export function parseHttpRequestWithResult(
  raw: string,
  options: ParseHttpRequestOptions = {},
): ParseHttpRequestResult {
  const lines = normalizeLines(raw);

  const firstLineIndex = findFirstNonEmptyLineIndex(lines);
  if (firstLineIndex < 0) {
    return { success: false, error: 'Empty content', isResponse: false };
  }

  const firstLine = lines[firstLineIndex];
  if (isHttpResponseLine(firstLine)) {
    return {
      success: false,
      error:
        'This is an HTTP response, not a request. Conversion is only available for HTTP requests.',
      isResponse: true,
    };
  }

  const requestMatch = firstLine.match(HTTP_REQUEST_LINE_REGEX);
  if (!requestMatch) {
    return {
      success: false,
      error:
        'Unable to parse HTTP request. Expected format: METHOD /path HTTP/1.1',
      isResponse: false,
    };
  }

  const method = requestMatch[1];
  const path = requestMatch[2];
  const httpVersion = normalizeParsedHttpVersion(requestMatch[3]);

  if (!isValidRequestTarget(method, path)) {
    return {
      success: false,
      error:
        'Unable to parse HTTP request. Expected format: METHOD /path HTTP/1.1',
      isResponse: false,
    };
  }

  const { headers, rawHeaders, bodyStartCharIndex } = parseHeaderBlock({
    lines,
    startIndex: firstLineIndex + 1,
    text: raw,
    captureRawHeaders: options.captureRawHeaders ?? false,
    enforceHttp2PseudoHeaderOrdering: isHttp2ParsedVersion(httpVersion),
  });

  const { hostHeader, pseudoAuthority, pseudoScheme } =
    extractRequestHostAuthorityScheme(headers);

  const body = extractMessageBody({
    raw,
    bodyStartCharIndex,
  });

  const url = buildRequestUrl({
    path,
    hostHeader,
    pseudoAuthority,
    pseudoScheme,
    originalUrl: options.originalUrl,
  });

  return {
    success: true,
    data: {
      method,
      url,
      path,
      httpVersion,
      headers,
      rawHeaders: rawHeaders.length > 0 ? rawHeaders : undefined,
      body,
    },
  };
}

export function parseHttpResponse(
  raw: string,
  options: ParseHttpResponseOptions = {},
): ParsedHttpResponse | null {
  const lines = normalizeLines(raw);

  const firstLineIndex = findFirstNonEmptyLineIndex(lines);
  if (firstLineIndex < 0) {
    return null;
  }

  const statusMatch = lines[firstLineIndex].match(STATUS_LINE_REGEX);
  if (!statusMatch) {
    return null;
  }

  const httpVersion = normalizeParsedHttpVersion(statusMatch[1]);
  const statusCode = Number.parseInt(statusMatch[2], 10);
  const statusMessage = statusMatch[3] ?? '';
  const { headers, rawHeaders, bodyStartCharIndex } = parseHeaderBlock({
    lines,
    startIndex: firstLineIndex + 1,
    text: raw,
    captureRawHeaders: options.captureRawHeaders ?? false,
    enforceHttp2PseudoHeaderOrdering: isHttp2ParsedVersion(httpVersion),
  });

  const body = extractMessageBody({
    raw,
    bodyStartCharIndex,
  });

  return {
    httpVersion,
    statusCode,
    statusMessage,
    headers,
    rawHeaders: rawHeaders.length > 0 ? rawHeaders : undefined,
    body,
  };
}

export function parseHttpResponsePacket(
  rawPacket: string | Uint8Array,
): ParsedHttpResponsePacket | null {
  const packetBuffer =
    typeof rawPacket === 'string'
      ? new TextEncoder().encode(rawPacket)
      : rawPacket;
  if (packetBuffer.length === 0) {
    return null;
  }

  const headerDelimiterMatch = findHeaderBodyDelimiter(packetBuffer);
  if (!headerDelimiterMatch) {
    return null;
  }

  const { index: headerEndIndex, length: delimiterLength } =
    headerDelimiterMatch;
  const headerText = new TextDecoder().decode(
    packetBuffer.subarray(0, headerEndIndex),
  );
  const bodyBuffer = packetBuffer.subarray(headerEndIndex + delimiterLength);
  const parsed = parseHttpResponse(headerText, { captureRawHeaders: true });
  if (!parsed) {
    return null;
  }

  return {
    ...parsed,
    body: new TextDecoder().decode(bodyBuffer),
    bodyBuffer,
    rawPacket: packetBuffer,
  };
}

function findHeaderBodyDelimiter(
  packetBuffer: Uint8Array,
): { index: number; length: number } | null {
  const crlfDelimiter = new TextEncoder().encode('\r\n\r\n');
  const crlfIndex = indexOfSubarray(packetBuffer, crlfDelimiter);
  if (crlfIndex > 0) {
    return { index: crlfIndex, length: crlfDelimiter.length };
  }

  const lfDelimiter = new TextEncoder().encode('\n\n');
  const lfIndex = indexOfSubarray(packetBuffer, lfDelimiter);
  if (lfIndex > 0) {
    return { index: lfIndex, length: lfDelimiter.length };
  }

  return null;
}

function indexOfSubarray(source: Uint8Array, search: Uint8Array): number {
  if (search.length === 0 || source.length < search.length) {
    return -1;
  }

  outer: for (let index = 0; index <= source.length - search.length; index++) {
    for (let offset = 0; offset < search.length; offset++) {
      if (source[index + offset] !== search[offset]) {
        continue outer;
      }
    }
    return index;
  }

  return -1;
}

export function formatHttpRequest(
  request: HttpRequestModel,
  options: FormatHttpMessageOptions = {},
): string {
  if (!request.httpVersion?.trim()) {
    throw new Error('HTTP version is required to format an HTTP request');
  }

  const lineEnding = options.lineEnding ?? '\n';
  const formattedHeaders = formatDisplayHeaderLines({
    headers: request.headers,
    rawHeaders: request.rawHeaders,
  }).join(lineEnding);
  const cleanMethod = request.method.replace(/[\r\n]/g, '').trim();
  if (!HTTP_TOKEN_REGEX.test(cleanMethod)) {
    throw new Error('HTTP method must be a valid RFC 7230 token');
  }
  let normalizedVersion = parseStrictFormatHttpVersion(request.httpVersion);
  const effectiveHttpVersion = resolveEffectiveHttpVersion({
    httpVersion: request.httpVersion,
    headers: request.rawHeaders ?? request.headers,
  });
  if (effectiveHttpVersion !== normalizeHarHttpVersion(request.httpVersion)) {
    normalizedVersion = parseStrictFormatHttpVersion(
      normalizeHttpVersionNumber(effectiveHttpVersion),
    );
  }
  const requestPath = extractRequestPath(request.url);
  const requestLine = `${cleanMethod} ${requestPath} HTTP/${normalizedVersion}`;
  const headerBlock = formattedHeaders
    ? `${requestLine}${lineEnding}${formattedHeaders}`
    : requestLine;

  if (request.body) {
    return `${headerBlock}${lineEnding}${lineEnding}${request.body}`;
  }

  return `${headerBlock}${lineEnding}${lineEnding}`;
}

/** Syntactic check: integer 100–999 for HTTP status-line (3DIGIT). HAR status 0 is invalid. */
export function isValidHttpStatusCode(status: number): boolean {
  return Number.isInteger(status) && status >= 100 && status <= 999;
}

export function formatHttpResponse(
  response: HttpResponseModel,
  options: FormatHttpMessageOptions = {},
): string {
  if (!response.httpVersion?.trim()) {
    throw new Error('HTTP version is required to format an HTTP response');
  }

  const lineEnding = options.lineEnding ?? '\n';
  const formattedHeaders = formatDisplayHeaderLines({
    headers: response.headers,
    rawHeaders: response.rawHeaders,
  }).join(lineEnding);
  let normalizedVersion = parseStrictFormatHttpVersion(response.httpVersion);
  const effectiveHttpVersion = resolveEffectiveHttpVersion({
    httpVersion: response.httpVersion,
    headers: response.rawHeaders ?? response.headers,
  });
  if (effectiveHttpVersion !== normalizeHarHttpVersion(response.httpVersion)) {
    normalizedVersion = parseStrictFormatHttpVersion(
      normalizeHttpVersionNumber(effectiveHttpVersion),
    );
  }
  if (!isValidHttpStatusCode(response.statusCode)) {
    throw new Error('HTTP status code must be a 3-digit RFC 7230 status-code');
  }
  const reason = response.statusMessage ?? '';
  const statusLine = `HTTP/${normalizedVersion} ${response.statusCode}${reason ? ` ${reason}` : ''}`;
  const headerBlock = formattedHeaders
    ? `${statusLine}${lineEnding}${formattedHeaders}`
    : statusLine;

  if (response.body) {
    return `${headerBlock}${lineEnding}${lineEnding}${response.body}`;
  }

  return `${headerBlock}${lineEnding}${lineEnding}`;
}

export function parseHttpTextToToolArgs({
  text,
  originalUrl,
  originalCredentialId,
}: {
  text: string;
  originalUrl: string;
  originalCredentialId?: string;
}): Record<string, unknown> | null {
  const parsed = parseHttpRequest(text, { originalUrl });
  if (!parsed) {
    return null;
  }

  const headers = Object.fromEntries(
    Object.entries(parsed.headers).filter(
      ([key]) => key.toLowerCase() !== 'host' && !key.startsWith(':'),
    ),
  ) as Record<string, string>;

  const result: Record<string, unknown> = {
    url: parsed.url,
    options: {
      method: parsed.method.toUpperCase(),
      ...(Object.keys(headers).length > 0 ? { headers } : {}),
      ...(parsed.body.length > 0 ? { body: parsed.body } : {}),
    },
  };

  if (originalCredentialId) {
    result.credentialId = originalCredentialId;
  }

  return result;
}
